import React from "react";
import gql from "graphql-tag";
import { Link } from "@reach/router";
import { useQuery } from "@apollo/react-hooks";
import produce from "immer";
import { InView } from "react-intersection-observer";
import ReactPlaceholder from "react-placeholder";
import { RectShape } from "react-placeholder/lib/placeholders";
import { Container, Box, Image, Row, Column } from "@atoms";
import { useStoreAppValue } from "@hooks";
import { RailsUrl, renderIf } from "@services";
import { CollectionRender, COLLECTION_TOP_SPACING } from "@organisms";
import { StoreFrontViewFragment } from "./__generated__/StoreFrontViewFragment";
// import styled from "@emotion/styled";

// == Types ================================================================

interface IProps {
  storefront: StoreFrontViewFragment;
  isHome: boolean;
}

// == Constants ============================================================

const FRAGMENTS = {
  fields: gql`
    fragment StoreFrontViewFragment on StoreFront {
      id
      name
      headerImage
      collectionsPaginated(first: 5) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            id
            ...CollectionRenderFragment
          }
        }
      }
    }
    ${CollectionRender.fragments.fields}
  `,
};

const DEFAULT_PROPS = {};

// == Placeholder

const PlaceholderRow = () => (
  <Row mb={4}>
    <Column width={[1 / 2, 1 / 2, 1 / 4]}>
      <RectShape color="#E0E0E0" style={{ width: "100%", height: 150, marginRight: 0 }} />
    </Column>
    <Column width={[1 / 2, 1 / 2, 1 / 4]}>
      <RectShape color="#E0E0E0" style={{ width: "100%", height: 150, marginRight: 0 }} />
    </Column>
    <Column width={[1 / 2, 1 / 2, 1 / 4]}>
      <RectShape color="#E0E0E0" style={{ width: "100%", height: 150, marginRight: 0 }} />
    </Column>
    <Column width={[1 / 2, 1 / 2, 1 / 4]}>
      <RectShape color="#E0E0E0" style={{ width: "100%", height: 150, marginRight: 0 }} />
    </Column>
  </Row>
);

const CollectionPlaceholder = () => (
  <ReactPlaceholder
    showLoadingAnimation
    color="#E0E0E0"
    customPlaceholder={PlaceholderRow()}
    ready={false}
  />
);

// == More collections

const STORE_FRONT_COLLECTIONS_QUERY = gql`
  query StoreFrontCollections($id: ID!, $afterCursor: String, $carouselItems: Int = 10) {
    StoreFront(id: $id) {
      id
      collectionsPaginated(first: 5, after: $afterCursor) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            ...CollectionRenderFragment
          }
        }
      }
    }
  }
  ${CollectionRender.fragments.fields}
`;

interface IStoreCollectionsProps {
  storeId: string;
  afterCursor: string;
}

const StoreCollections = ({ storeId, afterCursor }: IStoreCollectionsProps) => {
  const { loading, data, error, fetchMore } = useQuery(STORE_FRONT_COLLECTIONS_QUERY, {
    variables: { id: storeId, afterCursor },
  });

  if (loading) {
    return (
      <Box mt={COLLECTION_TOP_SPACING}>
        <CollectionPlaceholder />
      </Box>
    );
  }
  if (error || !data) return null;

  const { collectionsPaginated } = data.StoreFront;

  return (
    <>
      <Box mb={COLLECTION_TOP_SPACING} mt={COLLECTION_TOP_SPACING}>
        {collectionsPaginated.edges.map((edge, index) => (
          <CollectionRender collection={edge.node} key={edge.node.id} showTopSpacing={index > 0} />
        ))}
      </Box>

      {collectionsPaginated.pageInfo.hasNextPage ? (
        <InView
          as="div"
          rootMargin="100px 0px 0px 0px"
          onChange={(inView: boolean) => {
            if (inView) {
              fetchMore({
                query: STORE_FRONT_COLLECTIONS_QUERY,
                variables: {
                  id: storeId,
                  afterCursor: collectionsPaginated.pageInfo.endCursor,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  return produce(previousResult, (draft) => {
                    draft.StoreFront.collectionsPaginated.pageInfo =
                      fetchMoreResult.StoreFront.collectionsPaginated.pageInfo;

                    draft.StoreFront.collectionsPaginated.edges = [
                      ...previousResult.StoreFront.collectionsPaginated.edges,
                      ...fetchMoreResult.StoreFront.collectionsPaginated.edges,
                    ];
                  });
                },
              });
            }
          }}
        >
          <CollectionPlaceholder />
        </InView>
      ) : null}
    </>
  );
};

// == Component ============================================================

const StoreFrontView = ({ storefront, isHome }: IProps) => {
  const { collectionsPaginated, name, id } = storefront;
  return (
    <Container isMainContainer pt={0}>
      {!!storefront.headerImage && (
        <Box mb={COLLECTION_TOP_SPACING}>
          <Image alt={`${storefront.name}-content-item`} src={storefront.headerImage} />
        </Box>
      )}

      {collectionsPaginated.edges.map((edge, index) => (
        <CollectionRender collection={edge.node} key={edge.node.id} showTopSpacing={index > 0} />
      ))}

      {collectionsPaginated.pageInfo.hasNextPage ? (
        <StoreCollections afterCursor={collectionsPaginated.pageInfo.endCursor} storeId={id} />
      ) : null}
    </Container>
  );
};

StoreFrontView.fragments = FRAGMENTS;
StoreFrontView.defaultProps = DEFAULT_PROPS;

export default StoreFrontView;

// == Styles ===============================================================
